import {DataTableColumn} from "mantine-datatable";
import {I18n} from "@aws-amplify/core";
import {Text} from '@mantine/core';
import {Link, NavLink} from 'react-router-dom';
import {CustomerSubscription, SubscriptionItem} from "../types.ts";
import {SubscriptionStatusBadge} from '../profile/SubscriptionStatusBadge.tsx';
import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {moduleConfig} from '../config';

// The read-mode columns; RecordsTable appends the Actions column (repo CLAUDE.md).
//
// Sortable (§3a): Customer on the customer's name, Status on the enum's text, Created as
// ISO-8601. The Subscription column is NOT sortable: it shows a list of items, and a list
// has no single value to order rows by.
//
// `context` 'customer' is the table on a customer's page, where every row is that
// customer's, so the Customer column is left out.
export const useCustomerSubscriptionsTableColumns = (context: 'customer' | 'reports' = 'reports') => {

    const columns: DataTableColumn<CustomerSubscription>[] = [
        {
            accessor: 'items',
            title: I18n.get('Subscription'),
            // A real link into the detail page (FNP-322): keyboard-reachable, and edits go
            // through the detail page's Edit button rather than an in-row drawer.
            render: (record: CustomerSubscription) => {
                const items = Array.isArray(record.items?.items) ? record.items.items : [];

                return (
                    <NavLink to={`${moduleConfig.basePath}/${record.id}`} style={{textDecoration: 'none'}}>
                        {items.length === 0
                            ? <Text c="blue" size="sm">{I18n.get('Empty subscription')}</Text>
                            : items.slice(0, 2)
                                   .map((item: SubscriptionItem, index: number) => (
                                       <Text key={index} size="sm" c="blue">
                                           {item.price?.product?.name || `Price ${item.priceId}`} (x{item.quantity})
                                       </Text>
                                   ))}
                        {items.length > 2 && <Text size="xs" c="dimmed">+{items.length - 2} {I18n.get('more')}</Text>}
                    </NavLink>
                );
            }
        },
        ...(context === 'reports' ? [{
            accessor: 'customer.name',
            title: I18n.get('Customer'),
            sortable: true,
            render: (record: CustomerSubscription) => (
                <Link to={`/customers/${record.customerId}`} style={{textDecoration: 'none'}}>
                    <Text c="blue">
                        {record.customer?.name ?? record.customerId}
                    </Text>
                </Link>
            )
        }] : []),
        {
            accessor: 'status',
            title: I18n.get('Status'),
            sortable: true,
            render: (record: CustomerSubscription) => (
                <SubscriptionStatusBadge record={record}/>
            )
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            sortable: true,
            render: (record: CustomerSubscription) => formatTimestamp(record.created),
        },
    ];

    return {
        columns,
    }
}
